import { COMPUTED_COLUMNS, isComputedKey } from "./rowExtras";

/**
 * The unit-record columns that stay folded under "More columns" until a viewer opens a
 * group: the CFO's 2026-10-13 text fields and the 2026-10-14 computed columns (keys `__x_*`,
 * which never come from the layout). Everyday columns — lease dates, insurance expiry,
 * deposits the sheet always showed — are not listed, so they stay where they were.
 */
export const EXTRA_COLUMN_GROUPS = [
  { id: "unitDetails", label: "Unit details", keys: ["floor", "bed", "bath", "affordableUnit", "unitCondition", "scheduledRepairs"] },
  { id: "term", label: "Term", keys: ["originalLeaseStart", "__x_leaseKind"] },
  { id: "deposits", label: "Deposits", keys: ["petDeposit", "__x_totalDeposit"] },
  { id: "insurance", label: "Insurance", keys: ["__x_insurance", "insuranceProvider", "insurancePolicyNo", "insuredNames", "insuranceReviewed"] },
  { id: "utilities", label: "Utilities", keys: ["submeteringAccount", "landlordCharges"] },
  { id: "leaseExtras", label: "Lease extras", keys: ["__x_addendums", "__x_incentives", "__x_padThisMonth", "__x_futureLease"] },
  { id: "leasePlanning", label: "Lease planning", keys: ["incentives", "expectedActionAtLeaseEnd", "tenantRating", "__x_aiSuggestion"] },
];

/**
 * Pure: the layout's columns with the computed ones slotted in — each after the stored
 * column it belongs beside when that column exists, otherwise just before Status. Safe to
 * call on a list that already has them.
 */
export function withComputedColumns(columns) {
  const base = (columns || []).filter((c) => !isComputedKey(c.key));
  const out = [...base];
  const trailing = [];
  for (const def of COMPUTED_COLUMNS) {
    const col = { key: def.key, label: def.label, isVirtual: true, isComputed: true };
    const at = def.after ? out.findIndex((c) => c.key === def.after) : -1;
    if (at >= 0) out.splice(at + 1, 0, col);
    else trailing.push(col);
  }
  const statusAt = out.findIndex((c) => String(c.key).toLowerCase() === "status");
  if (statusAt >= 0) out.splice(statusAt, 0, ...trailing);
  else out.push(...trailing);
  return out;
}

const GROUP_OF_KEY = new Map(EXTRA_COLUMN_GROUPS.flatMap((g) => g.keys.map((k) => [k, g.id])));

/** The extra group a column belongs to, or null for an everyday column. */
export const extraGroupOf = (key) => GROUP_OF_KEY.get(key) || null;

const STORAGE_KEY = "mastersheet.extraColumnGroups";

/** Group ids the viewer has switched on. Storage can be blocked or cleared: then none. */
export const loadShownGroups = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(saved) ? saved.filter((id) => EXTRA_COLUMN_GROUPS.some((g) => g.id === id)) : [];
  } catch (e) {
    return [];
  }
};

export const saveShownGroups = (ids) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch (e) {
    // A viewer without storage simply starts folded next time.
  }
};

/** Pure: the columns to draw, and the groups this sheet actually has columns for. */
export const applyExtraGroups = (columns, shownGroups) => ({
  visibleColumns: columns.filter((c) => {
    const group = extraGroupOf(c.key);
    return !group || shownGroups.includes(group);
  }),
  availableGroups: EXTRA_COLUMN_GROUPS.filter((g) => columns.some((c) => extraGroupOf(c.key) === g.id)),
});
